import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals"

import {
  WEB_VITALS_PATH,
  type WebVitalMetric,
  type WebVitalRating,
  type WebVitalReport,
  beaconWebVital,
} from "@/api/webVitals"
import { isEmbed } from "@/util/embed"
import { logEvent } from "@/util/telemetry"

// Guards against a remount/hot-reload registering observers twice and
// double-counting samples.
let started = false

// `web-vitals` names are uppercase; the backend allowlist and Prometheus
// `metric` label are lowercase.
const METRIC_NAMES: Partial<Record<Metric["name"], WebVitalMetric>> = {
  CLS: "cls",
  FCP: "fcp",
  INP: "inp",
  LCP: "lcp",
  TTFB: "ttfb",
}

function isWebVitalRating(rating: string): rating is WebVitalRating {
  return rating === "good" || rating === "needs-improvement" || rating === "poor"
}

// Vitals are finalized on page hide/unload, so prefer `navigator.sendBeacon`
// (survives unload, unlike fetch/XHR); fall back to the API client when the
// beacon is unavailable or rejected.
async function sendReport(report: WebVitalReport): Promise<void> {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([JSON.stringify(report)], { type: "application/json" })
      if (navigator.sendBeacon(WEB_VITALS_PATH, blob)) {
        return
      }
    }
  } catch (error) {
    logEvent("error", "Failed to beacon web vital via sendBeacon", {
      sentryContext: "WebVitals",
      fingerprint: ["web-vitals-beacon-failed", report.metric],
      extra: { error, metric: report.metric },
    })
  }

  const result = await beaconWebVital(report)
  if (result.isErr) {
    const { message, status, code } = result.error
    logEvent("error", "Failed to beacon web vital", {
      sentryContext: "WebVitals",
      fingerprint: ["web-vitals-beacon-failed", report.metric],
      extra: { error: { message, status, code }, metric: report.metric },
    })
  }
}

function reportMetric(metric: Metric): void {
  const name = METRIC_NAMES[metric.name]
  if (name === undefined || !isWebVitalRating(metric.rating)) {
    return
  }
  if (!Number.isFinite(metric.value) || metric.value < 0) {
    return
  }
  void sendReport({ metric: name, value: metric.value, rating: metric.rating })
}

// Beacons real-user Web Vitals for the authenticated dashboard. Observers read
// buffered entries, so registering after mount still captures initial-load
// metrics. No-op in development, outside the browser, and inside embeds (which
// authenticate by token and lack the dashboard session cookie the beacon needs).
export function initWebVitals(): void {
  if (started || process.env.NODE_ENV === "development") {
    return
  }
  if (typeof window === "undefined" || isEmbed()) {
    return
  }
  started = true

  try {
    onLCP(reportMetric)
    onINP(reportMetric)
    onCLS(reportMetric)
    onFCP(reportMetric)
    onTTFB(reportMetric)
  } catch (error) {
    logEvent("error", "Failed to initialize web vitals tracking", {
      sentryContext: "WebVitals",
      fingerprint: ["web-vitals-init-failed"],
      extra: { error },
    })
  }
}
